import "./App.css";
import { Route, Routes, BrowserRouter as Router } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Toaster } from "react-hot-toast";
import Login from "./pages/auth/Login";
import Dashboard from "./pages/dashboard";

import Products from "./pages/catalog/products";
import Services from "./pages/services";
import Orders from "./pages/sales/orders";
import OrderDetails from "./pages/sales/orders/OrderDetails";
import Coupons from "./pages/customers/coupons";
// import ProtectedRoutes from './routes/ProtectedRoutes';
import RoleBasedRoute from "./routes/RoleBasedRoute";
import Reports from "./pages/reports";
import Blogs from "./pages/blogs";
import AddProduct from "./pages/catalog/products/AddProduct";
import Taxes from "./pages/taxes";
import Brands from "./pages/brands";
import Frames from "./pages/frames";
import EditProduct from "./pages/catalog/products/EditProduct";
import LensSize from "./pages/lensSize";
import Layout from "./components/layout/index";
import SalesRoute from "./routes/SalesRoute";
import ScrollTop from "./routes/ScrollTop";
import Customers from "./pages/customers/customersList";
import "react-datepicker/dist/react-datepicker.css";
import Category from "./pages/category";
import { SingleProduct } from "./pages/catalog/products/SingleProduct";
import ProductUpload from "./pages/productupload";
import AddCategory from "./pages/category/AddCategory";
import EditCategory from "./pages/category/EditCategory";
import IngredientsList from "./pages/catalog/ingredients/IngredientsList";
import ProductConcernsList from "./pages/catalog/concerns/ProductConcernsList";
import AddProductConcern from "./pages/catalog/concerns/AddProductConcern";
import AddIngredient from "./pages/catalog/ingredients/AddIngredient";
import EditProductConcern from "./pages/catalog/concerns/EditProductConcern";
import EditIngredient from "./pages/catalog/ingredients/EditIngredient";
import FaqList from "./pages/catalog/faqs/FaqList";
import ReviewsList from "./pages/reviews/ReviewsList";
import UsersListing from "./pages/masters/users";
import AddUser from "./pages/masters/users/AddUser";
import EditUser from "./pages/masters/users/EditUser";
import SalesReports from "./pages/sales/reports";
import CertificatesList from "./pages/certificates/CertificatesList";
import AddCertificate from "./pages/certificates/AddCertificate";
import EditCertificate from "./pages/certificates/EditCertificate";
import BannersList from "./pages/banners/BannersList";
import AddBanner from "./pages/banners/AddBanner";
import EditBanner from "./pages/banners/EditBanner";
import BusinessApplicationsList from "./pages/customers/business-applications";
import BusinessApplicationDetail from "./pages/customers/business-applications/BusinessApplicationDetail";
import CashOnDeliverySettings from "./pages/catalog/codSettings";
import WalletSettings from "./pages/settings/WalletSettings";
import OrderSettings from "./pages/settings/OrderSettings";
import ProductsVariants from "./pages/catalog/products/variants/ProductsVariants";
import CreateConfig from "./pages/catalog/products/variants/CreateConfig";
import EditConfig from "./pages/catalog/products/variants/EditConfig";
import { ProductConfigurationProvider } from "./providers/ProductConfigurationProvider";
import AttributesList from "./pages/catalog/attributes/AttributesList";
import ThresholdSettings from "./pages/settings/ThresholdSettings";

const queryClient = new QueryClient();
function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Toaster />
      <Router>
        <ScrollTop />
        <Routes>
          {/* Public routes */}
          <Route path="/login" exact element={<Login />} />
          <Route path="/" exact element={<Login />} />

          {/* Layout for authenticated routes */}
          <Route element={<Layout />}>
            {/* For both admin and user */}
            <Route element={<RoleBasedRoute allowedRoles={["admin"]} />}>
              <Route path="/masters/users" element={<UsersListing />} />
              <Route path="/masters/users/add-user" element={<AddUser />} />
              <Route
                path="/masters/users/edit-user/:userId"
                element={<EditUser />}
              />
            </Route>

            {/* for only admin */}
            <Route
              element={
                <RoleBasedRoute
                  allowedRoles={["admin", "brand_handler", "user"]}
                />
              }
            >
              <Route path="dashboard" element={<Dashboard />} />

              <Route path="sales/invoices" exact element={<Dashboard />} />
              <Route path="sales/returns" exact element={<Dashboard />} />
              <Route path="sales/reports" exact element={<SalesReports />} />
              <Route path="sales/orders" exact element={<Orders />} />
              <Route
                path="sales/orders/:order_id"
                exact
                element={<OrderDetails />}
              />
              {/* PDF view route placeholder, implement PDF page as needed */}
              <Route
                path="orders/:order_id/pdf"
                exact
                element={
                  <div style={{ padding: "2rem" }}>
                    PDF Viewer Page (implement as needed)
                  </div>
                }
              />

              {/* Category routes */}
              <Route path="catalog/category" exact element={<Category />} />
              <Route
                path="catalog/category/add-category"
                exact
                element={<AddCategory />}
              />
              <Route
                path="catalog/category/edit-category/:categoryId"
                exact
                element={<EditCategory />}
              />

              {/* Ingredients */}
              <Route
                path="catalog/ingredients"
                exact
                element={<IngredientsList />}
              />
              <Route
                path="catalog/ingredients/add-ingredient"
                exact
                element={<AddIngredient />}
              />
              <Route
                path="catalog/ingredients/edit-ingredient/:ingredientId"
                exact
                element={<EditIngredient />}
              />

              {/* Product concerns */}
              <Route
                path="catalog/concerns"
                exact
                element={<ProductConcernsList />}
              />
              <Route
                path="catalog/concerns/add-concern"
                exact
                element={<AddProductConcern />}
              />
              <Route
                path="catalog/concerns/edit-concern/:concernId"
                exact
                element={<EditProductConcern />}
              />

              <Route element={<ProductConfigurationProvider />}>
                {/* Product routes */}
                <Route path="catalog/products" exact element={<Products />} />
                <Route
                  path="catalog/products/add-product"
                  exact
                  element={<AddProduct />}
                />

                <Route
                  path="catalog/products/product-reviews/:id"
                  exact
                  element={<ReviewsList />}
                />
                <Route
                  path="catalog/products/edit-product/:id"
                  exact
                  element={<EditProduct />}
                />

                {/* Product Variants Routes */}
                <Route
                  path="catalog/products/:id/product-variants"
                  exact
                  element={<ProductsVariants />}
                />
                <Route
                  path="catalog/products/:id/product-variants/create"
                  exact
                  element={<CreateConfig />}
                />
                <Route
                  path="catalog/products/:id/product-variants/edit/:configId"
                  exact
                  element={<EditConfig />}
                />
              </Route>

              <Route
                path="catalog/attribute-values"
                exact
                element={<AttributesList />}
              />
              {/* Product uploads via excel and single product pricing */}
              <Route
                path="catalog/product-uploads"
                exact
                element={<ProductUpload />}
              />
              <Route
                path="catalog/products/single-product-pricing/:id"
                exact
                element={<SingleProduct />}
              />
              <Route
                path="catalog/cash-on-delivery-settings"
                exact
                element={<CashOnDeliverySettings />}
              />
              <Route path="catalog/faqs" exact element={<FaqList />} />

              {/* Customers */}
              <Route element={<RoleBasedRoute allowedRoles={["admin"]} />}>
                <Route path="customers/coupons" exact element={<Coupons />} />
                <Route
                  path="customers/all-customers"
                  exact
                  element={<Customers />}
                />
                <Route
                  path="customers/business-applications"
                  exact
                  element={<BusinessApplicationsList />}
                />
                <Route
                  path="customers/business-applications/:id"
                  exact
                  element={<BusinessApplicationDetail />}
                />
              </Route>

              {/* Blogs */}
              <Route path="system/blogs" exact element={<Blogs />} />

              {/* Brands */}
              <Route element={<RoleBasedRoute allowedRoles={["admin"]} />}>
                <Route path="filters/brands" exact element={<Brands />} />
              </Route>

              {/* Taxes */}
              <Route element={<RoleBasedRoute allowedRoles={["admin"]} />}>
                <Route path="sales/taxes" exact element={<Taxes />} />
              </Route>

              {/* Certificates */}
              <Route element={<RoleBasedRoute allowedRoles={["admin"]} />}>
                <Route
                  path="masters/certificates"
                  exact
                  element={<CertificatesList />}
                />
                <Route
                  path="masters/certificates/add-certificate"
                  exact
                  element={<AddCertificate />}
                />
                <Route
                  path="masters/certificates/edit-certificate/:certificateId"
                  exact
                  element={<EditCertificate />}
                />
              </Route>

              {/* Banners */}
              <Route element={<RoleBasedRoute allowedRoles={["admin"]} />}>
                <Route path="masters/banners" exact element={<BannersList />} />
                <Route
                  path="masters/banners/add-banner"
                  exact
                  element={<AddBanner />}
                />
                <Route
                  path="masters/banners/edit-banner/:bannerId"
                  exact
                  element={<EditBanner />}
                />
              </Route>

              {/* Settings */}
              <Route element={<RoleBasedRoute allowedRoles={["admin"]} />}>
                <Route
                  path="settings/wallet"
                  exact
                  element={<WalletSettings />}
                />
                <Route path="settings/orders" exact element={<OrderSettings />} />
                <Route
                  path="settings/threshold"
                  exact
                  element={<ThresholdSettings />}
                />
              </Route>
            </Route>
          </Route>
        </Routes>
      </Router>
      <ReactQueryDevtools initialIsOpen={false} />
    </QueryClientProvider>
  );
}

export default App;
